import React, { useRef, useState } from 'react';
import { Button, Col, message, Modal, Row, Space, Tag } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import List, { ListRef } from '../../components/Templates/List';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { brandApi } from '../../services/manager/brand';
import type { Brand } from '../../types/entity/brand';
import { BrandLabels } from '../../types/entity/brand';

type BrandFormValues = {
  sourceBrandId: string;
  name: string;
  englishName?: string;
  aliasesText?: string;
  status?: number;
};

const statusOptions = [
  { label: '启用', value: 1 },
  { label: '禁用', value: 0 },
];

const Brands: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    record: Brand | null;
  }>({
    open: false,
    mode: 'create',
    record: null,
  });

  const queryFields: FieldConfig[] = [
    { key: 'sourceBrandId', label: BrandLabels.sourceBrandId, type: 'string' },
    { key: 'name', label: BrandLabels.name, type: 'string' },
    { key: 'status', label: BrandLabels.status, type: 'select', options: statusOptions },
  ];

  const formFields: FormFieldConfig[] = [
    { name: 'sourceBrandId', label: BrandLabels.sourceBrandId, type: 'text', required: true },
    { name: 'name', label: BrandLabels.name, type: 'text', required: true },
    { name: 'englishName', label: BrandLabels.englishName, type: 'text', rules: [{ max: 100, message: '英文名最多100个字符' }] },
    {
      name: 'aliasesText',
      label: BrandLabels.aliases,
      type: 'textarea',
      fieldProps: { rows: 4 },
      rules: [{ max: 2000, message: '别名内容过长' }],
    },
    { name: 'status', label: BrandLabels.status, type: 'select', initialValue: 1, options: statusOptions },
  ];

  const columns: ColumnsType<Brand> = [
    { title: 'ID', dataIndex: 'id', width: 90, fixed: 'left' },
    { title: BrandLabels.sourceBrandId, dataIndex: 'sourceBrandId', width: 140, fixed: 'left' },
    {
      title: BrandLabels.name,
      dataIndex: 'name',
      width: 140,
      render: (value: string) => <span style={{ color: '#1677ff', fontWeight: 600 }}>{value || '-'}</span>,
    },
    {
      title: BrandLabels.englishName,
      dataIndex: 'englishName',
      width: 140,
      render: (value?: string) => value || '-',
    },
    {
      title: BrandLabels.aliases,
      dataIndex: 'aliases',
      width: 260,
      render: (_, record) => renderAliases(record.aliases),
    },
    {
      title: BrandLabels.status,
      dataIndex: 'status',
      width: 100,
      render: (value?: number) => (
        <Tag color={value === 1 ? 'green' : 'red'}>
          {value === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      width: 180,
      render: (_, record) => formatDateTime(record.createdAt || record.created_at),
    },
    {
      title: '更新时间',
      width: 180,
      render: (_, record) => formatDateTime(record.updatedAt || record.updated_at),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>
            查看
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => setModalState({ open: true, mode: 'edit', record })}
          >
            编辑
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleDelete = (record: Brand) => {
    Modal.confirm({
      title: '确认删除品牌',
      content: `确定删除品牌 "${record.name || '-'}" 吗？`,
      onOk: async () => {
        try {
          await brandApi.deleteById(String(record.id));
          message.success('删除成功');
          listRef.current?.reload();
        } catch (error: any) {
          message.error(error?.message || '删除失败');
        }
      },
    });
  };

  const handleView = (record: Brand) => {
    Modal.info({
      title: '品牌详情',
      width: 720,
      content: (
        <div style={{ marginTop: 16 }}>
          <DetailRow label="ID" value={String(record.id)} />
          <DetailRow label={BrandLabels.sourceBrandId} value={record.sourceBrandId || '-'} />
          <DetailRow label={BrandLabels.name} value={record.name || '-'} />
          <DetailRow label={BrandLabels.englishName} value={record.englishName || '-'} />
          <DetailRow label={BrandLabels.aliases} valueNode={renderAliases(record.aliases)} />
          <DetailRow
            label={BrandLabels.status}
            valueNode={<Tag color={record.status === 1 ? 'green' : 'red'}>{record.status === 1 ? '启用' : '禁用'}</Tag>}
          />
          <DetailRow label="创建时间" value={formatDateTime(record.createdAt || record.created_at)} />
          <DetailRow label="更新时间" value={formatDateTime(record.updatedAt || record.updated_at)} />
        </div>
      ),
    });
  };

  return (
    <>
      <List<Brand>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>品牌管理</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
              新增品牌
            </Button>
          </div>
        }
        queryFields={queryFields}
        fetcher={async (params) => {
          const res = await brandApi.list(params);
          return { rows: res.data.items, pagination: res.data.pagination };
        }}
        buildParams={buildListParams}
        columns={columns}
        rowKey={(row) => String(row.id)}
        tableProps={{ scroll: { x: 'max-content' } }}
      />

      <UpsertModal<Brand>
        title={modalState.mode === 'create' ? '新增品牌' : '编辑品牌'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        onCancel={() => setModalState((prev) => ({ ...prev, open: false }))}
        onSuccess={() => {
          setModalState((prev) => ({ ...prev, open: false }));
          listRef.current?.reload();
        }}
        createApi={(data) => brandApi.create(data)}
        updateApi={(id, data) => brandApi.updateById(id, data)}
        formFields={formFields}
        beforeEdit={(record) => ({
          ...record,
          aliasesText: normalizeAliases(record.aliases).join('\n'),
        })}
        beforeSubmit={(values: BrandFormValues) => ({
          sourceBrandId: values.sourceBrandId?.trim(),
          name: values.name?.trim(),
          englishName: values.englishName?.trim() || '',
          aliases: normalizeAliases(values.aliasesText),
          status: values.status,
        })}
      />
    </>
  );
};

const buildListParams = (query: ForgeQuery) => {
  const params: Record<string, any> = {
    page: query.page,
    pageSize: query.pageSize,
  };

  if (query.filter) {
    Object.entries(query.filter).forEach(([key, value]) => {
      if (value && typeof value === 'object' && 'value' in value) {
        params[key] = (value as any).value;
      }
    });
  }

  return params;
};

const normalizeAliases = (value: Brand['aliases'] | string | undefined): string[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  const raw = String(value).trim();
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // ignore parse failure and fallback to split
  }

  return raw
    .split(/[\n,，]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const renderAliases = (value: Brand['aliases']) => {
  const aliases = normalizeAliases(value);
  if (!aliases.length) {
    return '-';
  }
  return (
    <Space size={[4, 4]} wrap>
      {aliases.map((alias) => (
        <Tag key={alias}>{alias}</Tag>
      ))}
    </Space>
  );
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN');
};

const DetailRow: React.FC<{ label: string; value?: string; valueNode?: React.ReactNode }> = ({ label, value, valueNode }) => (
  <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
    <Col span={7} style={{ fontWeight: 600 }}>{label}:</Col>
    <Col span={17}>{valueNode ?? value ?? '-'}</Col>
  </Row>
);

export default Brands;
